/**
 * Agent platform policy-crafting events — catalog v9 additions (commerce-platform
 * Phase 5, plan §5 "Event catalog v9" / §6 Step 3 / §9 audit coverage).
 *
 * - `agents:recipe-attached`           — a recipe was attached to a factory and
 *   its capability policies were crafted (the ONLY policy-crafting path for
 *   agents — recipes/compilers, never hand-written statements, §9).
 * - `agents:store-defaults-provisioned` — the per-store predefined agent
 *   provisioning succeeded (cashier; manager is STOP-AND-ASK OQ-1/A12 and is
 *   NEVER provisioned here).
 * - `agents:policy-subset-violation`   — the assigner-subset rule rejected a
 *   grant: "an agent can never hold permissions its assigner lacks" (exit
 *   criterion). Every rejection is audited (plan §3 L29).
 *
 * All three are `auditable: true` — the plan's audit-coverage gate (§9)
 * requires denial + provisioning + attach to be reconstructable from the
 * audit trail.
 */
import {
  asEventRecord,
  defineEvent,
  optionalString,
  requireString,
  requireStringArray,
  type EventDefinition,
  type EventSchema,
} from './registry';

export interface AgentRecipeAttachedPayload extends Record<string, unknown> {
  factoryId: string;
  recipeId: string;
  /** Owner of the crafted policies — the authenticated user that attached the recipe. */
  assignedTo: string;
  /** Policy ids created by the attach (crafted via the authorization engine). */
  policyIds: string[];
  orgId?: string;
}

export interface AgentStoreDefaultsProvisionedPayload extends Record<string, unknown> {
  orgId: string;
  storeId: string;
  workspaceId?: string;
  factoryId: string;
  /** Predefined agent role that was provisioned (cashier only until OQ-1/A12 clears). */
  agentRole: string;
  assignedTo: string;
  policyIds: string[];
  /** True when the idempotent key (store) already had a provisioned factory. */
  alreadyProvisioned: boolean;
}

export interface AgentPolicySubsetViolationPayload extends Record<string, unknown> {
  actorUserId: string;
  action: string;
  resource: string;
  reason: string;
  targetFactoryId?: string;
  recipeId?: string;
}

const RECIPE_ATTACHED_TYPE = 'agents:recipe-attached';
const recipeAttachedSchema: EventSchema<AgentRecipeAttachedPayload> = {
  parse(data) {
    const record = asEventRecord(data, RECIPE_ATTACHED_TYPE);
    const orgId = optionalString(record.orgId, 'orgId', RECIPE_ATTACHED_TYPE);
    return {
      factoryId: requireString(record.factoryId, 'factoryId', RECIPE_ATTACHED_TYPE),
      recipeId: requireString(record.recipeId, 'recipeId', RECIPE_ATTACHED_TYPE),
      assignedTo: requireString(record.assignedTo, 'assignedTo', RECIPE_ATTACHED_TYPE),
      policyIds: requireStringArray(record.policyIds, 'policyIds', RECIPE_ATTACHED_TYPE),
      ...(orgId === undefined ? {} : { orgId }),
    };
  },
};

export const AGENT_RECIPE_ATTACHED_EVENT = defineEvent<AgentRecipeAttachedPayload>({
  type: RECIPE_ATTACHED_TYPE,
  version: 1,
  schema: recipeAttachedSchema,
  auditable: true,
  description: 'A recipe was attached to an agent factory and its capability policies were crafted via the authorization engine.',
  since: '1.11.0',
});

const STORE_DEFAULTS_TYPE = 'agents:store-defaults-provisioned';
const storeDefaultsSchema: EventSchema<AgentStoreDefaultsProvisionedPayload> = {
  parse(data) {
    const record = asEventRecord(data, STORE_DEFAULTS_TYPE);
    const workspaceId = optionalString(record.workspaceId, 'workspaceId', STORE_DEFAULTS_TYPE);
    const alreadyProvisioned = record.alreadyProvisioned;
    if (typeof alreadyProvisioned !== 'boolean') {
      throw new Error(`${STORE_DEFAULTS_TYPE}: field "alreadyProvisioned" must be a boolean`);
    }
    return {
      orgId: requireString(record.orgId, 'orgId', STORE_DEFAULTS_TYPE),
      storeId: requireString(record.storeId, 'storeId', STORE_DEFAULTS_TYPE),
      ...(workspaceId === undefined ? {} : { workspaceId }),
      factoryId: requireString(record.factoryId, 'factoryId', STORE_DEFAULTS_TYPE),
      agentRole: requireString(record.agentRole, 'agentRole', STORE_DEFAULTS_TYPE),
      assignedTo: requireString(record.assignedTo, 'assignedTo', STORE_DEFAULTS_TYPE),
      policyIds: requireStringArray(record.policyIds, 'policyIds', STORE_DEFAULTS_TYPE),
      alreadyProvisioned,
    };
  },
};

export const AGENT_STORE_DEFAULTS_PROVISIONED_EVENT = defineEvent<AgentStoreDefaultsProvisionedPayload>({
  type: STORE_DEFAULTS_TYPE,
  version: 1,
  schema: storeDefaultsSchema,
  auditable: true,
  description: 'Predefined store-default agents were provisioned (cashier; manager pending OQ-1/A12).',
  since: '1.11.0',
});

const SUBSET_VIOLATION_TYPE = 'agents:policy-subset-violation';
const subsetViolationSchema: EventSchema<AgentPolicySubsetViolationPayload> = {
  parse(data) {
    const record = asEventRecord(data, SUBSET_VIOLATION_TYPE);
    const targetFactoryId = optionalString(record.targetFactoryId, 'targetFactoryId', SUBSET_VIOLATION_TYPE);
    const recipeId = optionalString(record.recipeId, 'recipeId', SUBSET_VIOLATION_TYPE);
    return {
      actorUserId: requireString(record.actorUserId, 'actorUserId', SUBSET_VIOLATION_TYPE),
      action: requireString(record.action, 'action', SUBSET_VIOLATION_TYPE),
      resource: requireString(record.resource, 'resource', SUBSET_VIOLATION_TYPE),
      reason: requireString(record.reason, 'reason', SUBSET_VIOLATION_TYPE),
      ...(targetFactoryId === undefined ? {} : { targetFactoryId }),
      ...(recipeId === undefined ? {} : { recipeId }),
    };
  },
};

export const AGENT_POLICY_SUBSET_VIOLATION_EVENT = defineEvent<AgentPolicySubsetViolationPayload>({
  type: SUBSET_VIOLATION_TYPE,
  version: 1,
  schema: subsetViolationSchema,
  auditable: true,
  description: 'The assigner-subset rule denied an agent grant: the agent would have held a permission its assigner lacks.',
  since: '1.11.0',
});

export const AGENTS_EVENTS: Readonly<Record<string, EventDefinition>> = {
  [AGENT_RECIPE_ATTACHED_EVENT.type]: AGENT_RECIPE_ATTACHED_EVENT,
  [AGENT_STORE_DEFAULTS_PROVISIONED_EVENT.type]: AGENT_STORE_DEFAULTS_PROVISIONED_EVENT,
  [AGENT_POLICY_SUBSET_VIOLATION_EVENT.type]: AGENT_POLICY_SUBSET_VIOLATION_EVENT,
};
